// src/services/http.ts
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios'

import { wp, withWpLanguageParam } from '@/lib/api'

/**
 * Features:
 * - retry (exponential backoff + jitter)
 * - cancel (AbortController)
 * - dedupe (in-flight request sharing + ref counting)
 * - multi-namespace support via options.client (AxiosInstance)
 */

export type RetryStrategy = {
  retries?: number // default 2
  baseDelayMs?: number // default 250
  maxDelayMs?: number // default 4000
  jitter?: boolean // default true
  retryOnStatuses?: number[] // default [408, 429, 500, 502, 503, 504]
  retryOnNetworkError?: boolean // default true
  // Optional custom predicate
  shouldRetry?: (error: unknown, attempt: number) => boolean
}

export type HttpOptions = {
  /**
   * Choose which Axios client to use (namespace/baseURL).
   * Defaults to wp from @/lib/api
   */
  client?: AxiosInstance

  dedupe?: boolean // default true
  dedupeKey?: string // override dedupe key

  /**
   * Local abort signal: cancels THIS caller's interest.
   * If multiple callers share the same request (dedupe), the underlying request
   * is aborted only when the last caller cancels (ref-count reaches 0).
   */
  signal?: AbortSignal

  retry?: RetryStrategy
}

export type CancelableRequest<T> = {
  promise: Promise<T>
  cancel: () => void
  key: string
}

type InFlightEntry<T = unknown> = {
  key: string
  controller: AbortController
  refs: number
  promise: Promise<T>
}

const inFlight = new Map<string, InFlightEntry<any>>()

function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>()
  const replacer = (_k: string, v: any) => {
    if (v && typeof v === 'object') {
      if (seen.has(v)) return '[Circular]'
      seen.add(v)
      if (Array.isArray(v)) return v
      return Object.keys(v)
        .sort()
        .reduce<Record<string, any>>((acc, key) => {
          acc[key] = v[key]
          return acc
        }, {})
    }
    return v
  }

  try {
    return JSON.stringify(value ?? null, replacer)
  } catch {
    return String(value)
  }
}

function makeDedupeKey(
  client: AxiosInstance,
  config: AxiosRequestConfig,
  explicit?: string,
): string {
  if (explicit) return explicit

  const method = (config.method ?? 'get').toLowerCase()

  // Prefer request config.baseURL, else fall back to client defaults.
  const baseURL = config.baseURL ?? client.defaults.baseURL ?? ''
  const url = config.url ?? ''

  const params = stableStringify(config.params)
  const data = stableStringify(config.data)

  return `${method}::${baseURL}${url}::p=${params}::d=${data}`
}

function isAbortError(err: unknown): boolean {
  const e = err as any
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError' ||
    e?.message?.toLowerCase?.().includes('canceled') ||
    e?.message?.toLowerCase?.().includes('cancelled')
  )
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (!ms) return Promise.resolve()

  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'))

    const t = setTimeout(() => {
      cleanup()
      resolve()
    }, ms)

    const onAbort = () => {
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }

    const cleanup = () => {
      clearTimeout(t)
      signal?.removeEventListener('abort', onAbort)
    }

    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

function calcBackoff(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number,
  jitter: boolean,
): number {
  const exp = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt - 1))
  if (!jitter) return exp
  return Math.floor(Math.random() * exp) // full jitter
}

function defaultRetryConfig(): Required<RetryStrategy> {
  return {
    retries: 2,
    baseDelayMs: 250,
    maxDelayMs: 4000,
    jitter: true,
    retryOnStatuses: [408, 429, 500, 502, 503, 504],
    retryOnNetworkError: true,
    shouldRetry: () => false,
  }
}

function shouldRetryByDefault(
  err: unknown,
  attempt: number,
  retry: Required<RetryStrategy>,
): boolean {
  if (retry.shouldRetry(err, attempt)) return true
  if (isAbortError(err)) return false

  const ax = err as AxiosError
  const status = ax?.response?.status

  // Network error: no response
  if (!ax?.response && retry.retryOnNetworkError) return true

  if (typeof status === 'number' && retry.retryOnStatuses.includes(status)) return true

  return false
}

async function runWithRetry<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  controller: AbortController,
  retryCfg?: RetryStrategy,
): Promise<T> {
  const retry = { ...defaultRetryConfig(), ...retryCfg }

  let lastErr: unknown

  for (let attempt = 1; attempt <= retry.retries + 1; attempt++) {
    if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')

    try {
      return await fn(controller.signal)
    } catch (err) {
      lastErr = err

      // last attempt already used
      if (attempt >= retry.retries + 1) break

      const ok = shouldRetryByDefault(err, attempt, retry)
      if (!ok) throw err

      const delay = calcBackoff(attempt, retry.baseDelayMs, retry.maxDelayMs, retry.jitter)
      await sleep(delay, controller.signal)
    }
  }

  throw lastErr
}

function detachEntry(key: string, entry: InFlightEntry<any>) {
  const current = inFlight.get(key)
  if (current === entry) inFlight.delete(key)
}

export function request<T = unknown>(
  config: AxiosRequestConfig,
  options: HttpOptions = {},
): CancelableRequest<T> {
  const client = options.client ?? wp
  const requestConfig = client === wp ? withWpLanguageParam(config) : config
  const key = makeDedupeKey(client, requestConfig, options.dedupeKey)
  const dedupe = options.dedupe !== false // default true

  // If dedupe enabled and in-flight exists: share promise + ref counting
  if (dedupe) {
    const existing = inFlight.get(key)
    if (existing) {
      existing.refs++

      const cancel = () => {
        existing.refs = Math.max(0, existing.refs - 1)
        if (existing.refs === 0) existing.controller.abort()
      }

      if (options.signal) {
        if (options.signal.aborted) cancel()
        else options.signal.addEventListener('abort', cancel, { once: true })
      }

      return { key, promise: existing.promise as Promise<T>, cancel }
    }
  }

  // New in-flight entry
  const controller = new AbortController()
  const entry: InFlightEntry<T> = {
    key,
    controller,
    refs: 1,
    promise: Promise.resolve() as any,
  }

  const cancel = () => {
    entry.refs = Math.max(0, entry.refs - 1)
    if (entry.refs === 0) controller.abort()
  }

  if (options.signal) {
    if (options.signal.aborted) cancel()
    else options.signal.addEventListener('abort', cancel, { once: true })
  }

  entry.promise = runWithRetry<T>(
    async (signal) => {
      const resp: AxiosResponse<T> = await client.request<T>({
        ...requestConfig,
        signal,
      })
      return resp.data
    },
    controller,
    options.retry,
  ).finally(() => {
    detachEntry(key, entry)
  })

  if (dedupe) inFlight.set(key, entry)

  return { key, promise: entry.promise, cancel }
}

/**
 * Convenience helpers
 * Note: pass namespace client in options.client when needed.
 */
export function get<T = unknown>(
  url: string,
  config: Omit<AxiosRequestConfig, 'url' | 'method'> = {},
  options?: HttpOptions,
): CancelableRequest<T> {
  return request<T>({ ...config, url, method: 'GET' }, options)
}

export function post<T = unknown>(
  url: string,
  data?: unknown,
  config: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'> = {},
  options?: HttpOptions,
): CancelableRequest<T> {
  return request<T>({ ...config, url, data, method: 'POST' }, options)
}

export function put<T = unknown>(
  url: string,
  data?: unknown,
  config: Omit<AxiosRequestConfig, 'url' | 'method' | 'data'> = {},
  options?: HttpOptions,
): CancelableRequest<T> {
  return request<T>({ ...config, url, data, method: 'PUT' }, options)
}

export function del<T = unknown>(
  url: string,
  config: Omit<AxiosRequestConfig, 'url' | 'method'> = {},
  options?: HttpOptions,
): CancelableRequest<T> {
  return request<T>({ ...config, url, method: 'DELETE' }, options)
}

/**
 * Optional: cancel all in-flight requests (e.g. on logout)
 */
export function cancelAllInFlight() {
  for (const [, entry] of inFlight) entry.controller.abort()
  inFlight.clear()
}
